import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { B2C_SITE_ORIGIN } from "@danishagro/shared/src/constants/urlRoot.constants";

interface PageLocationContextType {
    displayedAtLocation: string | null;
    isLoading: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pageData: any | null;
}

const PageLocationContext = createContext<PageLocationContextType | undefined>(undefined);
export { PageLocationContext };

// Helper function to extract location from page data
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const extractLocationFromPageData = (data: any): string => {
    if (data.alias) {
        return data.alias;
    } else if (data.pageAlias === "Product" && data.product?.webName) {
        return `Product details page | ${data.product.webName}`;
    } else if (data.pageAlias === "GroupProduct" && data.groupProduct?.webName) {
        return `Product listing page | ${data.groupProduct.webName}`;
    } else if (data.category?.name) {
        return `Category page | ${data.category.name}`;
    } else if (data.pageAlias) {
        return data.pageAlias;
    } else {
        return "Customer Portal";
    }
};

export const PageLocationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const location = useLocation();
    const [displayedAtLocation, setDisplayedAtLocation] = useState<string | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [pageData, setPageData] = useState<any | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    // Fetch page data when path changes
    useEffect(() => {
        const fetchPageLocation = async () => {
            const path = location.pathname === "/" ? "/" : location.pathname;

            // Clear the location and data immediately when path changes
            setDisplayedAtLocation(null);
            setPageData(null);
            setIsLoading(true);

            try {
                // Fetch from API
                const response = await fetch(`${B2C_SITE_ORIGIN}/api${path}`);
                const data = await response.json();

                // Store the full page data
                setPageData(data);

                // Extract and set the location
                const newLocation = extractLocationFromPageData(data);
                setDisplayedAtLocation(newLocation);
                setIsLoading(false);
            } catch (error) {
                console.error(
                    "[PageLocationContext] Error fetching from API, falling back to globalThis.pageContent:",
                    error
                );

                // Fallback: Use globalThis.pageContent if API fails
                if (globalThis.pageContent) {
                    setPageData(globalThis.pageContent);
                    const newLocation = extractLocationFromPageData(globalThis.pageContent);
                    setDisplayedAtLocation(newLocation);
                } else {
                    // Last resort: use default
                    setPageData(undefined);
                    setDisplayedAtLocation("Customer Portal");
                }
                setIsLoading(false);
            }
        };

        fetchPageLocation();
    }, [location.pathname]);

    return (
        <PageLocationContext.Provider value={{ displayedAtLocation, isLoading, pageData }}>
            {children}
        </PageLocationContext.Provider>
    );
};

export const usePageLocationContext = (): PageLocationContextType => {
    const context = useContext(PageLocationContext);
    if (context === undefined) {
        throw new Error("usePageLocationContext must be used within a PageLocationProvider");
    }
    return context;
};